import {
    MutableRefObject,
    useCallback,
    useContext,
    useEffect,
    useRef,
} from 'react';

import { useAiSmartFilterData } from 'component/Search/Filter/AiSmartFilter/__hooks/useAiSmartFilterData';

import { Feature } from 'config/features';
import { ContactContext } from 'context/ContactContext';

import { useSetSearchAtom } from 'hooks/form/useSetSearchAtom';
import { useFeature } from 'hooks/useFeature';
import { useLocalStorage } from 'hooks/useLocalStorage';

import { filterAtom } from 'data/Atom/Filter';

import { convertUrlToFilter } from 'utility/Convert/Search/urlToFilter';
import { CommunicationContext } from 'utility/Provide/Context/PageContext';

import { ChattyClientDetailOfferParams } from 'types/chatty/offerParams';
import {
    BridgeEvent,
    BridgeInfoDetails,
    BridgeResponse,
    BridgeResponseContext,
    BridgeResponseEvents,
    ConnectionInfoDetails,
} from 'types/legacy/wireframe';

import { FilterInformation } from 'proto/Search/FilterMeta';

import { bridgeConfig } from '../__config/settings';
import { fetchFilterDataPromptCached } from '../__helper/fetchFilterDataPromptCached';
import { useBuildFilterResults } from '../__helper/filterResults';
import { getPageScope, getServiceCode } from '../__helper/getChatBotConfig';
import { hasChanges } from '../__helper/hasChanges';
import { ChattyContextEvent } from '../types';

import { useFallbackUrl } from './useFallbackUrl';
import { useUpdateCsCode } from './useUpdateCsCode';

const chattySearchCorrelationIdRef: MutableRefObject<string> = { current: '' };
const correlationIdStorageKey = 'chattySearchCorrelationId';

export const useChatty = (offerData?: ChattyClientDetailOfferParams) => {
    const { chattyConfig, check24Wireframe } = useContext(ContactContext);
    const buildFilterData = useAiSmartFilterData();
    const { _env } = useContext(CommunicationContext);
    const { updateCsCode } = useUpdateCsCode();
    const updateFilter = useSetSearchAtom(filterAtom);
    const { setItemPhpSessionScope } = useLocalStorage();
    const connectionInfoDetailRef = useRef<ConnectionInfoDetails>({
        serviceCode: getServiceCode(),
        pageScope: getPageScope(),
        plugin: bridgeConfig.plugin,
        filterData: '',
    });
    if (offerData) {
        connectionInfoDetailRef.current = {
            ...connectionInfoDetailRef.current,
            offer: offerData,
        };
    }
    const bridgeInfoDetailRef = useRef<BridgeInfoDetails>({
        product_key: bridgeConfig.product_id,
        type: BridgeEvent.Event,
        context: {
            ...connectionInfoDetailRef.current,
        },
    });
    const bridgeResponseRef = useRef<BridgeResponse>({
        product_key: bridgeConfig.product_id,
        type: BridgeEvent.Response,
        correlation_id: '',
        data: {},
    });
    const isChattyActive = useFeature(Feature.Chatty);
    const isForeignDomain = _env.foreignDomain;
    const isChatActive = isChattyActive && !isForeignDomain;
    const openHelpUrl = useFallbackUrl();
    const buildFilterResults = useBuildFilterResults();
    const chattyFallBack = useCallback(() => {
        openHelpUrl();
    }, [openHelpUrl]);

    const updateBridgeContext = useCallback(() => {
        bridgeInfoDetailRef.current = {
            ...bridgeInfoDetailRef.current,
            context: {
                ...connectionInfoDetailRef.current,
            },
        };
    }, [connectionInfoDetailRef, bridgeInfoDetailRef]);
    const updateConnectionInfoDetailRef = useCallback(
        (data?: Partial<ConnectionInfoDetails>) => {
            connectionInfoDetailRef.current = {
                ...data,
                plugin: bridgeConfig.plugin,
                serviceCode: connectionInfoDetailRef.current.serviceCode,
                pageScope: getPageScope(),
            };
            if (data?.serviceCode) {
                connectionInfoDetailRef.current.serviceCode = data.serviceCode;
            }
            if (offerData) {
                connectionInfoDetailRef.current.offer = offerData;
            }

            updateBridgeContext();
        },
        [updateBridgeContext, offerData]
    );

    const updateDetails = useCallback(
        async (data?: Partial<ConnectionInfoDetails>, openChatty?: boolean) => {
            if (!isChatActive) {
                return;
            }
            requestAnimationFrame(async () => {
                const isChattyOpen =
                    !!check24Wireframe?.loader?.chatty?.checkActiveChatty();
                if (getPageScope() === 3 && (openChatty || isChattyOpen)) {
                    const filterData = await fetchFilterDataPromptCached(
                        buildFilterData()
                    );

                    data = {
                        ...data,
                        filterData: filterData?.filterDataPrompt,
                    };
                }

                const isChattyConnectionOpen =
                    !!check24Wireframe?.chatty?.isActive();
                if (hasChanges(connectionInfoDetailRef.current, _env, data)) {
                    updateConnectionInfoDetailRef(data);
                    const chattySendContext = new CustomEvent(
                        ChattyContextEvent.SEND_CONTEXT,
                        {
                            detail: { context: bridgeInfoDetailRef.current },
                        }
                    );
                    const chattySendBackgroundContext = new CustomEvent(
                        ChattyContextEvent.BACKGROUND_UPDATE_CONTEXT,
                        {
                            detail: {
                                chatty: {
                                    configname: chattyConfig?.name,
                                    connectionInfoDetails:
                                        connectionInfoDetailRef.current,
                                },
                            },
                        }
                    );

                    if (
                        !!check24Wireframe?.loader?.chatty?.listenerInitilized
                    ) {
                        document.dispatchEvent(
                            isChattyConnectionOpen
                                ? chattySendContext
                                : chattySendBackgroundContext
                        );
                    }
                }
                if (!isChattyOpen && openChatty) {
                    setTimeout(() => {
                        check24Wireframe?.loader.openChatbot(chattyConfig);
                    }, 200);
                }
            });
        },
        [
            buildFilterData,
            check24Wireframe,
            updateConnectionInfoDetailRef,
            _env,
            chattyConfig,
            isChatActive,
        ]
    );

    const triggerSearch = useCallback(
        (correlationId: string, url?: string) => {
            if (url) {
                if (
                    window.location.href.includes('/suche/hotel') &&
                    url.includes('/suche/hotel')
                ) {
                    chattySearchCorrelationIdRef.current = correlationId;
                    updateFilter(convertUrlToFilter(decodeURIComponent(url)));
                } else {
                    sessionStorage.setItem(
                        correlationIdStorageKey,
                        correlationId
                    );
                    window.location.href = url;
                }
            }
        },
        [updateFilter]
    );

    const sendContext = useCallback(() => {
        if (!!check24Wireframe?.loader?.chatty?.checkActiveChatty()) {
            setTimeout(() => {
                const chattySendContext = new CustomEvent(
                    ChattyContextEvent.SEND_CONTEXT,
                    {
                        detail: { context: bridgeResponseRef.current },
                    }
                );

                document.dispatchEvent(chattySendContext);
            }, 500);
        }
    }, [check24Wireframe]);

    const responseBridgeUpdate = useCallback(
        async (data: BridgeResponseContext) => {
            const responseData = data?.context;

            if (responseData.action) {
                if (
                    responseData?.action ===
                        BridgeResponseEvents.ApplyVoucher &&
                    !!responseData?.data?.cs_code
                ) {
                    const updateData = await updateCsCode(
                        responseData?.data?.cs_code as string
                    );

                    bridgeResponseRef.current = {
                        ...bridgeResponseRef.current,
                        correlation_id: responseData.correlation_id,
                        data: updateData,
                    };

                    sendContext();
                }
                if (responseData?.action === BridgeResponseEvents.GetContext) {
                    bridgeResponseRef.current = {
                        ...bridgeResponseRef.current,
                        correlation_id: responseData.correlation_id,
                        data: {
                            serviceCode: getServiceCode(),
                            pageScope: getPageScope(),
                            plugin: bridgeConfig.plugin,
                        },
                    };

                    if (offerData) {
                        bridgeResponseRef.current.data.offer = offerData;
                    }

                    sendContext();
                }

                if (
                    responseData?.action ===
                        BridgeResponseEvents.TriggerSearch &&
                    responseData.data.search_url
                ) {
                    triggerSearch(
                        responseData.correlation_id,
                        responseData.data.search_url as string
                    );
                }
            }
        },
        [updateCsCode, sendContext, offerData, triggerSearch]
    );

    const pushFilterResults = useCallback(
        async (success: boolean, filterInformation?: FilterInformation) => {
            await updateDetails();
            if (chattySearchCorrelationIdRef.current) {
                if (success && filterInformation) {
                    bridgeResponseRef.current = {
                        ...bridgeResponseRef.current,
                        correlation_id: chattySearchCorrelationIdRef.current,
                        data: {
                            trigger_search: {
                                success,
                                filter_results:
                                    buildFilterResults(filterInformation),
                            },
                        },
                    };
                } else {
                    bridgeResponseRef.current = {
                        ...bridgeResponseRef.current,
                        correlation_id: chattySearchCorrelationIdRef.current,
                        data: {
                            trigger_search: {
                                success: false,
                                filter_results: [],
                            },
                        },
                    };
                }
                chattySearchCorrelationIdRef.current = '';

                sendContext();
            }
        },
        [buildFilterResults, sendContext, updateDetails]
    );

    const openChatty = useCallback(
        (data?: Partial<ConnectionInfoDetails>) => {
            if (isChatActive) {
                setItemPhpSessionScope('closeInactiveTabChatty', '1');
                updateDetails(data, true);
            } else {
                chattyFallBack();
            }
        },
        [updateDetails, isChatActive, chattyFallBack, setItemPhpSessionScope]
    );

    const initCorrelationId = useCallback(() => {
        chattySearchCorrelationIdRef.current =
            sessionStorage.getItem(correlationIdStorageKey) || '';
        sessionStorage.removeItem(correlationIdStorageKey);
    }, []);

    useEffect(() => {
        const listener = function (event: StorageEvent) {
            if (
                event.key &&
                event.key.indexOf('closeInactiveTabChatty') > -1 &&
                check24Wireframe?.loader.chatty?.checkActiveChatty()
            ) {
                check24Wireframe?.loader.chatty?.close();
            }
        };
        window.addEventListener('storage', listener);

        return () => {
            window.removeEventListener('storage', listener);
        };
    }, [check24Wireframe?.loader.chatty]);

    return {
        openChatty,
        updateDetails,
        responseBridgeUpdate,
        connectionInfoDetailRef,
        check24Wireframe,
        pushFilterResults,
        initCorrelationId,
    };
};
